import type { CaseView } from '@chemoflow/contracts';
import type { CaseChange, RemoteChangeKind } from '@chemoflow/domain';
import { fmt } from './format.js';

/** 他端末の変化を知らせるトーストの中身。表示は画面側で行う。 */
export interface Notice {
  icon: string;
  title: string;
  sub: string;
}

const ACTION: Record<RemoteChangeKind, { text: string; icon: string }> = {
  'clerk-confirm': { text: '外来が確定しました', icon: 'check' },
  'clerk-change': { text: '外来が変更しました', icon: 'sync' },
  'clerk-stop': { text: '外来が中止しました', icon: 'close' },
  'clerk-undo': { text: '外来が入力を取り消しました', icon: 'undo' },
  'pharm-start': { text: '薬剤部が調製を開始しました', icon: 'play_arrow' },
  'pharm-undo': { text: '薬剤部が調製開始を取り消しました', icon: 'undo' },
  'stop-ack': { text: '薬剤部が中止連絡を確認しました', icon: 'done_all' },
  'admin-start': { text: 'ケモ室が投与を開始しました', icon: 'water_drop' },
  'admin-undo': { text: 'ケモ室が投与開始を取り消しました', icon: 'undo' },
  'pharm-done': { text: '薬剤部が調製を完了しました', icon: 'done_all' },
  'pharm-done-undo': { text: '薬剤部が調製完了を取り消しました', icon: 'undo' },
  'admin-done': { text: 'ケモ室が投与を完了しました', icon: 'task_alt' },
  'admin-done-undo': { text: 'ケモ室が投与完了を取り消しました', icon: 'undo' },
};

/** 氏名は院内参照が切れているときは出さない（画面の他の場所と同じ扱い）。 */
const who = (c: CaseView): string =>
  c.patient
    ? `${c.patient.name}（${c.patient.patientId}）`
    : `氏名を表示できません（${c.surrogateKey}）`;

export function describeChange(change: CaseChange<CaseView>): Notice {
  const { kind, after, before } = change;
  const action = ACTION[kind];
  let sub: string;
  switch (kind) {
    case 'clerk-change':
    case 'clerk-stop':
      sub = after.clerk?.reason || fmt(after.clerk?.at ?? Date.now());
      break;
    case 'clerk-confirm':
      sub = fmt(after.clerk?.at ?? Date.now());
      break;
    case 'pharm-start':
      sub = fmt(after.pharm?.at ?? Date.now());
      break;
    case 'stop-ack':
      sub = fmt(after.stopAck?.at ?? Date.now());
      break;
    case 'admin-start':
      sub = fmt(after.admin?.at ?? Date.now());
      break;
    case 'pharm-done':
      sub = fmt(after.pharmDone?.at ?? Date.now());
      break;
    case 'admin-done':
      sub = fmt(after.adminDone?.at ?? Date.now());
      break;
    case 'clerk-undo':
      sub = before.pharm ? '調製開始後の取り消しです' : '入力前に戻りました';
      break;
    default:
      sub = '一覧で確認してください';
  }
  return { icon: action.icon, title: `${who(after)}：${action.text}`, sub };
}

/** 一度に多数の変化があったとき（復帰直後など）は1件にまとめる。 */
export const summaryNotice = (count: number): Notice => ({
  icon: 'notifications',
  title: `ほかの端末で ${count}件 の更新がありました`,
  sub: '一覧で確認してください',
});

/** 1回の取得でこの件数を超えたらまとめて知らせる。トーストは同時に3件までしか出さない。 */
export const MAX_INDIVIDUAL_NOTICES = 3;
